export const META_PIXEL_ID = "440690073330758";
export const META_EVENT_ID_STORAGE_KEY = "signup:meta:submission-event-id:v1";
export const META_ATTRIBUTION_STORAGE_KEY = "signup:meta:attribution:v1";
export const META_ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

let metaPixelInitialized = false;
let lastAdvancedMatchingSignature = "";
let advancedMatchingRequestId = 0;

const isBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

const isUuidV4 = value =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );

const isValidClickId = value =>
  typeof value === "string" &&
  value.length > 0 &&
  value.length <= 256 &&
  /^[A-Za-z0-9._~-]+$/.test(value);

const getStorage = type => {
  if (!isBrowser()) return null;

  try {
    return window[type];
  } catch (error) {
    return null;
  }
};

const getQueuedFbq = () => {
  if (!isBrowser()) return null;

  if (typeof window.fbq === "function") return window.fbq;

  try {
    const fbq = function fbq() {
      fbq.callMethod
        ? fbq.callMethod.apply(fbq, arguments)
        : fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    return fbq;
  } catch (error) {
    return null;
  }
};

export function initializeMetaPixel() {
  if (!isBrowser() || metaPixelInitialized) return;

  try {
    const hasExistingFbq = typeof window.fbq === "function";
    const fbq = getQueuedFbq();
    if (!fbq) return;

    let pixelAlreadyInitialized = false;

    if (hasExistingFbq) {
      if (typeof fbq.getState === "function") {
        const state = fbq.getState();
        pixelAlreadyInitialized =
          Array.isArray(state && state.pixels) &&
          state.pixels.some(pixel => String(pixel.id) === META_PIXEL_ID);
      }

      if (!pixelAlreadyInitialized && Array.isArray(fbq.queue)) {
        pixelAlreadyInitialized = fbq.queue.some(
          call =>
            call && call[0] === "init" && String(call[1]) === META_PIXEL_ID
        );
      }
    }

    if (!pixelAlreadyInitialized) {
      fbq("init", META_PIXEL_ID);
    }
    metaPixelInitialized = true;

    if (hasExistingFbq) return;

    if (
      document.querySelector(`script[data-meta-pixel-id="${META_PIXEL_ID}"]`) ||
      document.querySelector(
        'script[src*="connect.facebook.net"][src*="fbevents.js"]'
      )
    ) {
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    script.dataset.metaPixelId = META_PIXEL_ID;
    script.onerror = () => {};
    document.head.appendChild(script);
  } catch (error) {
    // Tracking protection, network failures, and CSP failures must never affect signup.
  }
}

export function trackMetaPageView() {
  if (!isBrowser() || typeof window.fbq !== "function") return;

  try {
    window.fbq("track", "PageView");
  } catch (error) {
    // Meta is optional browser telemetry.
  }
}

export function buildMetaAdvancedMatching(values = {}) {
  const matching = {};

  if (typeof values.em === "string" && values.em.trim() !== "") {
    matching.em = values.em.trim().toLowerCase();
  }

  ["fn", "ln"].forEach(field => {
    if (typeof values[field] === "string" && values[field].trim() !== "") {
      matching[field] = values[field]
        .toLowerCase()
        .replace(/[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/g, "");
    }
  });

  if (typeof values.zp === "string" && values.zp.trim() !== "") {
    matching.zp = values.zp
      .toLowerCase()
      .split("-", 1)[0]
      .trim();
  }

  if (values.ph != null) {
    const phone = String(values.ph)
      .replace(/\D/g, "")
      .replace(/^0+/, "");
    if (phone !== "") matching.ph = phone;
  }

  return matching;
}

export async function updateMetaAdvancedMatching(values) {
  if (!isBrowser() || typeof window.fbq !== "function") return false;

  const matching = buildMetaAdvancedMatching(values);
  if (Object.keys(matching).length === 0) return false;

  const cryptoApi = window.crypto;
  if (
    !cryptoApi ||
    !cryptoApi.subtle ||
    typeof cryptoApi.subtle.digest !== "function" ||
    typeof TextEncoder !== "function"
  ) {
    return false;
  }

  const requestId = ++advancedMatchingRequestId;

  try {
    const encoder = new TextEncoder();
    const hashedEntries = await Promise.all(
      Object.entries(matching).map(async ([field, value]) => {
        const digest = await cryptoApi.subtle.digest(
          "SHA-256",
          encoder.encode(value)
        );
        const hash = Array.from(new Uint8Array(digest), byte =>
          byte.toString(16).padStart(2, "0")
        ).join("");
        return [field, hash];
      })
    );

    if (requestId !== advancedMatchingRequestId) return false;

    const hashedMatching = hashedEntries.reduce((allFields, [field, hash]) => {
      allFields[field] = hash;
      return allFields;
    }, {});
    const signature = JSON.stringify(hashedMatching);
    if (signature === lastAdvancedMatchingSignature) return true;

    window.fbq("init", META_PIXEL_ID, hashedMatching);
    lastAdvancedMatchingSignature = signature;
    return true;
  } catch (error) {
    // Advanced matching must not interfere with data entry or form submission.
    return false;
  }
}

export function createMetaEventId() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;

  try {
    if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
      const eventId = cryptoApi.randomUUID();
      return isUuidV4(eventId) ? eventId : null;
    }

    if (cryptoApi && typeof cryptoApi.getRandomValues === "function") {
      const bytes = new Uint8Array(16);
      cryptoApi.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, byte =>
        byte.toString(16).padStart(2, "0")
      ).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
        12,
        16
      )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch (error) {
    return null;
  }

  return null;
}

export function getOrCreateMetaEventId() {
  const storage = getStorage("sessionStorage");
  if (!storage) return createMetaEventId();

  try {
    const savedEventId = storage.getItem(META_EVENT_ID_STORAGE_KEY);
    if (isUuidV4(savedEventId)) return savedEventId;
    if (savedEventId) storage.removeItem(META_EVENT_ID_STORAGE_KEY);

    const eventId = createMetaEventId();
    if (eventId) storage.setItem(META_EVENT_ID_STORAGE_KEY, eventId);
    return eventId;
  } catch (error) {
    return createMetaEventId();
  }
}

export function clearMetaEventId() {
  const storage = getStorage("sessionStorage");
  if (!storage) return;

  try {
    storage.removeItem(META_EVENT_ID_STORAGE_KEY);
  } catch (error) {
    // Storage availability must not change the form lifecycle.
  }
}

export function captureSignupAttribution() {
  if (!isBrowser()) return null;

  const storage = getStorage("localStorage");
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    const gclid = params.get("gclid");
    const hasClickIds = params.has("fbclid") || params.has("gclid");
    const attribution = {
      capturedAt: Date.now(),
      ...(isValidClickId(fbclid) ? { fbclid } : {}),
      ...(isValidClickId(gclid) ? { gclid } : {})
    };

    if (attribution.fbclid || attribution.gclid) {
      if (!storage) return null;
      storage.setItem(
        META_ATTRIBUTION_STORAGE_KEY,
        JSON.stringify(attribution)
      );
    }

    if (hasClickIds) {
      params.delete("fbclid");
      params.delete("gclid");
      const remainingSearch = params.toString();
      const cleanUrl = `${window.location.pathname}${
        remainingSearch ? `?${remainingSearch}` : ""
      }${window.location.hash}`;
      window.history.replaceState(window.history.state, "", cleanUrl);
    }

    if (attribution.fbclid || attribution.gclid) return attribution;
    return storage ? getSignupAttribution() : null;
  } catch (error) {
    return null;
  }
}

export function getSignupAttribution() {
  const storage = getStorage("localStorage");
  if (!storage) return null;

  try {
    const rawAttribution = storage.getItem(META_ATTRIBUTION_STORAGE_KEY);
    if (!rawAttribution) return null;

    const attribution = JSON.parse(rawAttribution);
    if (
      !attribution ||
      !Number.isFinite(attribution.capturedAt) ||
      Date.now() - attribution.capturedAt > META_ATTRIBUTION_TTL_MS ||
      Date.now() < attribution.capturedAt
    ) {
      storage.removeItem(META_ATTRIBUTION_STORAGE_KEY);
      return null;
    }

    const sanitizedAttribution = {
      capturedAt: attribution.capturedAt,
      ...(isValidClickId(attribution.fbclid)
        ? { fbclid: attribution.fbclid }
        : {}),
      ...(isValidClickId(attribution.gclid) ? { gclid: attribution.gclid } : {})
    };
    if (!sanitizedAttribution.fbclid && !sanitizedAttribution.gclid) {
      storage.removeItem(META_ATTRIBUTION_STORAGE_KEY);
      return null;
    }

    return sanitizedAttribution;
  } catch (error) {
    return null;
  }
}

export function getValidMetaCookies() {
  if (!isBrowser()) return {};

  try {
    const cookies = document.cookie.split(";").reduce((allCookies, cookie) => {
      const [name, ...value] = cookie.trim().split("=");
      if (name) allCookies[name] = decodeURIComponent(value.join("="));
      return allCookies;
    }, {});
    const identifiers = {};

    if (/^fb\.1\.\d+\.\d+$/.test(cookies._fbp || "")) {
      identifiers.fbp = cookies._fbp;
    }
    if (/^fb\.1\.\d+\.[A-Za-z0-9._~-]+$/.test(cookies._fbc || "")) {
      identifiers.fbc = cookies._fbc;
    }

    return identifiers;
  } catch (error) {
    return {};
  }
}

export function addMetaConversionPayload(payload) {
  const eventId = getOrCreateMetaEventId();
  const attribution = getSignupAttribution();
  const browserIdentifiers = getValidMetaCookies();
  if (
    !browserIdentifiers.fbc &&
    attribution &&
    attribution.fbclid &&
    Number.isFinite(attribution.capturedAt)
  ) {
    browserIdentifiers.fbc = `fb.1.${attribution.capturedAt}.${
      attribution.fbclid
    }`;
  }

  return {
    ...payload,
    ...(eventId ? { meta_event_id: eventId } : {}),
    ...browserIdentifiers,
    ...(attribution && attribution.fbclid
      ? { fbclid: attribution.fbclid }
      : {}),
    ...(attribution && attribution.gclid ? { gclid: attribution.gclid } : {})
  };
}

export async function postSignupFormWithMetaConversion(post, preparePayload) {
  let submittedMetaEventId = null;
  const response = await post(null, false, payload => {
    const payloadWithMetaConversion = addMetaConversionPayload(
      preparePayload(payload)
    );
    submittedMetaEventId = payloadWithMetaConversion.meta_event_id || null;
    return payloadWithMetaConversion;
  });

  return { response, submittedMetaEventId };
}

export async function trackMetaSignupFormSubmission(eventId, matching = {}) {
  if (!isUuidV4(eventId) || !isBrowser() || typeof window.fbq !== "function") {
    return;
  }

  try {
    await updateMetaAdvancedMatching(matching);
    window.fbq(
      "trackCustom",
      "SignupFormSubmission",
      {},
      {
        eventID: eventId
      }
    );
  } catch (error) {
    // A blocked Pixel must never turn a successful signup into a failed one.
  }
}

export function resetMetaConversionsForTests() {
  metaPixelInitialized = false;
  lastAdvancedMatchingSignature = "";
  advancedMatchingRequestId = 0;
}
